import { DateTime } from 'luxon';
import type { CreatedCalendarEvent } from '../components/ScheduleMeetingModal/types/hooks';
import { getCpaasApiBaseUrl } from '../config/authConfig';
import type {
  AccountConnections,
  ApiConfig,
  CalendarApiClient,
  OAuthConnection,
  PagedCalendarEvents,
} from '../types/api';
import { getMeetingUrl } from '../utils/environment';
import { copyInvitationText } from '../utils/meetingInvitation';
import { BaseApiClient } from './apiClient';
import { authStorageService } from './authStorageService';

type TimeMinMax = {
  timeMin: string;
  timeMax: string;
};

type CalendarDialInOptions = {
  phoneNumber?: string | null;
  pin?: string | null;
  hasLicenseError?: boolean;
};

type CalendarOption = 'outlook' | 'google' | 'other';

type CalendarEventFormData = {
  description: string;
  fromDate: string;
  fromTime: string;
  toDate: string;
  toTime: string;
  room: string;
  selectedColor?: string;
  password?: string;
};

type CreateCalendarEventInBackgroundParams = {
  calendarOption: CalendarOption;
  formData: CalendarEventFormData;
  dialIn?: CalendarDialInOptions;
  accountConnections?: AccountConnections;
};

const buildCalendarAuthError = (
  providerName: 'Google' | 'Microsoft',
  status: number,
  body: string,
): string | undefined => {
  const isAuthFailure =
    status === 401 &&
    /invalid credentials|authError|UNAUTHENTICATED|invalidauthenticationtoken|token is expired|token expired|lifetime validation failed|invalid_grant/i.test(
      body,
    );
  if (!isAuthFailure) {
    return undefined;
  }
  return `${providerName} calendar session expired. Please reconnect your calendar account.`;
};

const toIso = (date: string, time: string) => new Date(`${date}T${time}`).toISOString();
const toLocalDateTime = (date: string, time: string) => `${date}T${time}:00`;
const toPlainCalendarDescription = (text: string) =>
  text
    .replaceAll('\r', '')
    .split('\n')
    .map((line: string) => line.trim())
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

export class CalendarService extends BaseApiClient implements CalendarApiClient {
  private static readonly CPAAS_CALENDAR_BASE_URL = `${getCpaasApiBaseUrl()}/starhub/calendar`;

  private static readonly CALENDAR_GOOGLE_EVENTS_AND_LINK_API =
    'https://www.googleapis.com/calendar/v3/calendars';
  private static readonly CALENDAR_MICROSOFT_EVENTS_AND_LINK_API =
    'https://graph.microsoft.com/v1.0/me/calendarView';
  private static readonly CALENDAR_MICROSOFT_DELTA_API =
    'https://graph.microsoft.com/v1.0/me/calendarView/delta';
  private static readonly CALENDAR_MICROSOFT_LIST_EVENT_API =
    `https://graph.microsoft.com/v1.0/me/events`;
  private static readonly CALENDAR_GOOGLE_COLORS_API =
    'https://www.googleapis.com/calendar/v3/colors';
  private static readonly CALENDAR_MICROSOFT_MASTER_CATEGORIES_API =
    'https://graph.microsoft.com/v1.0/me/outlook/masterCategories';

  private static getAuthToken(): string {
    return authStorageService.getToken();
  }

  constructor(config?: Partial<ApiConfig>) {
    super({
      baseUrl: CalendarService.CPAAS_CALENDAR_BASE_URL,
      headers: {
        Authorization: `Bearer ${CalendarService.getAuthToken()}`,
        ...(config?.headers || {}),
      },
      ...config,
    });
  }

  async fetchCalendarTypes(): Promise<AccountConnections> {
    const response = await this.get<AccountConnections>(`/types`);
    return response.data;
  }

  async disconnectGoogleCalendarType(): Promise<string> {
    const response = await this.get<string>(`/disconnect/google`);
    return response.data;
  }

  async disconnectMicrosoftCalendarType(): Promise<string> {
    const response = await this.get<string>(`/disconnect/microsoft`);
    return response.data;
  }

  getTimeMinMax(activeTab: string, todayOnly?: boolean): TimeMinMax {
    let timeMin: string;
    let timeMax: string;
    const nowUtc = DateTime.now().toUTC();
    const startOfToday = nowUtc.startOf('day');

    if (todayOnly) {
      const endOfToday = nowUtc.endOf('day');
      timeMin = startOfToday.toISO();
      timeMax = endOfToday.toISO();
    } else if (activeTab === 'past') {
      // Anchor the past window from "now" so newly-ended events are included immediately.
      timeMin = nowUtc.minus({ months: 1 }).toISO();
      timeMax = nowUtc.toISO();
    } else {
      timeMin = nowUtc.toISO();
      timeMax = nowUtc.plus({ months: 1 }).toISO();
    }

    return { timeMin, timeMax };
  }

  private sortGooglePastEvents(
    events: PagedCalendarEvents['events'],
  ): PagedCalendarEvents['events'] {
    return [...events].sort((a, b) => {
      const aEnd = new Date(
        a.end?.dateTime ?? a.end?.date ?? a.start?.dateTime ?? a.start?.date ?? 0,
      ).getTime();
      const bEnd = new Date(
        b.end?.dateTime ?? b.end?.date ?? b.start?.dateTime ?? b.start?.date ?? 0,
      ).getTime();
      return bEnd - aEnd;
    });
  }

  private async collectGooglePastWindowEvents(
    fetchPage: (
      token?: string,
    ) => Promise<{ ok: true; json: { items?: unknown[]; nextPageToken?: string } } | { ok: false }>,
    initialItems: PagedCalendarEvents['events'],
    initialNextToken?: string,
  ): Promise<PagedCalendarEvents['events']> {
    let allItems = [...initialItems];
    let nextToken = initialNextToken;
    let pageCount = 0;

    while (nextToken && pageCount < 50) {
      const nextPageResult = await fetchPage(nextToken);
      if (!nextPageResult.ok) {
        break;
      }
      allItems = [
        ...allItems,
        ...((nextPageResult.json.items ?? []) as PagedCalendarEvents['events']),
      ];
      nextToken = nextPageResult.json.nextPageToken;
      pageCount += 1;
    }

    return this.sortGooglePastEvents(allItems);
  }

  async fetchGoogleCalendarEventsList(
    calendarType: OAuthConnection,
    activeTab: string,
    pageToken?: string,
    todayOnly?: boolean,
    maxResults?: number,
  ): Promise<PagedCalendarEvents> {
    const accessToken = calendarType?.login_link;

    const { timeMin, timeMax } = this.getTimeMinMax(activeTab, todayOnly);

    const params = new URLSearchParams({
      timeMin,
      timeMax,
      showDeleted: 'false',
      singleEvents: 'true',
      orderBy: 'startTime',
    });
    if (!todayOnly) {
      params.set('maxResults', String(maxResults ?? 5));
    }

    if (pageToken) {
      params.set('pageToken', pageToken);
    }

    const calendarId = calendarType?.email ?? 'primary';
    const encodedCalendarId = encodeURIComponent(calendarId);
    const fetchPage = async (token?: string) => {
      const pageParams = new URLSearchParams(params);
      if (token) {
        pageParams.set('pageToken', token);
      } else {
        pageParams.delete('pageToken');
      }

      const url = `${CalendarService.CALENDAR_GOOGLE_EVENTS_AND_LINK_API}/${encodedCalendarId}/events?${pageParams.toString()}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorBody = await response.text();
        console.error(
          '[CalendarService] Google Calendar fetch failed :',
          response.status,
          errorBody,
        );
        return {
          ok: false as const,
          authError: buildCalendarAuthError('Google', response.status, errorBody),
        };
      }

      const json = await response.json();
      return { ok: true as const, json };
    };

    const firstPageResult = await fetchPage(pageToken);
    if (!firstPageResult.ok) {
      return {
        events: [],
        nextPageToken: undefined,
        authError: firstPageResult.authError,
      };
    }

    const shouldCollectPastWindow = activeTab === 'past' && !todayOnly && !pageToken;
    let allItems = (firstPageResult.json.items ?? []) as PagedCalendarEvents['events'];
    const nextToken = firstPageResult.json.nextPageToken as string | undefined;

    if (activeTab === 'past') {
      allItems = shouldCollectPastWindow
        ? await this.collectGooglePastWindowEvents(fetchPage, allItems, nextToken)
        : this.sortGooglePastEvents(allItems);
    }

    return {
      events: this.updateGoogleEventsWithCalendarId(allItems, calendarId),
      nextPageToken: shouldCollectPastWindow ? undefined : nextToken,
      nextSyncToken: firstPageResult.json.nextSyncToken,
    };
  }

  async fetchGoogleCalendarEventsDelta(
    calendarType: OAuthConnection,
    syncToken: string,
    pageToken?: string,
  ): Promise<PagedCalendarEvents> {
    const accessToken = calendarType?.login_link;
    const calendarId = calendarType?.email ?? 'primary';
    const encodedCalendarId = encodeURIComponent(calendarId);
    const params = new URLSearchParams({
      syncToken,
      showDeleted: 'true',
    });
    if (pageToken) {
      params.set('pageToken', pageToken);
    }
    const url = `${CalendarService.CALENDAR_GOOGLE_EVENTS_AND_LINK_API}/${encodedCalendarId}/events?${params.toString()}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorBody = await response.text();
      console.error(
        '[CalendarService] Google Calendar delta fetch failed :',
        response.status,
        errorBody,
      );
      return {
        events: [],
        nextSyncToken: undefined,
        nextPageToken: undefined,
        authError: buildCalendarAuthError('Google', response.status, errorBody),
      };
    }

    const json = await response.json();
    return {
      events: this.updateGoogleEventsWithCalendarId(
        (json.items ?? []) as PagedCalendarEvents['events'],
        calendarId,
      ),
      nextSyncToken: json.nextSyncToken,
      nextPageToken: json.nextPageToken,
    };
  }

  async fetchMicrosoftCalendarEventsList(
    calendarType: OAuthConnection,
    activeTab: string,
    nextLink?: string,
    todayOnly?: boolean,
    topCount?: number,
  ): Promise<PagedCalendarEvents> {
    const accessToken = calendarType?.login_link;
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const { timeMin, timeMax } = this.getTimeMinMax(activeTab, todayOnly);

    let requestUrl: string;

    if (nextLink) {
      requestUrl = nextLink;
    } else {
      const url = new URL(`${CalendarService.CALENDAR_MICROSOFT_EVENTS_AND_LINK_API}`);

      url.searchParams.set('startDateTime', timeMin);
      url.searchParams.set('endDateTime', timeMax);
      const order = activeTab === 'past' ? 'desc' : 'asc';
      url.searchParams.set('$orderby', `start/dateTime ${order}`);
      if (!todayOnly) {
        url.searchParams.set('$top', String(topCount ?? 5));
      }
      url.searchParams.set(
        '$select',
        'subject,start,end,id,isCancelled,location,body,recurrence,showAs,organizer,webLink,attendees,isAllDay,categories',
      );

      requestUrl = url.toString();
    }

    const response = await fetch(requestUrl, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        Prefer: `outlook.timezone="${userTimeZone}"`,
      },
    });

    if (!response.ok) {
      const errorBody = await response.text();
      console.error(
        '[CalendarService] Microsoft Calendar fetch failed :',
        response.status,
        errorBody,
      );
      return {
        events: [],
        nextLink: undefined,
        authError: buildCalendarAuthError('Microsoft', response.status, errorBody),
      };
    }

    const json = await response.json();
    const events = json.value ?? [];

    // Keep a conservative guard for range start while honoring provider timezone.
    const rangeStart = DateTime.fromISO(timeMin).toUTC().toMillis();
    const filteredEvents = events.filter((event: Record<string, unknown>) => {
      const eventEnd = (event.end ?? {}) as {
        dateTime?: string;
        date?: string;
        timeZone?: string;
      };
      const endValue = eventEnd.dateTime ?? eventEnd.date;
      if (!endValue) {
        return false;
      }

      const parsedWithZone = eventEnd.timeZone
        ? DateTime.fromISO(endValue, { zone: eventEnd.timeZone })
        : DateTime.invalid('Missing event timezone');
      const parsedEnd = parsedWithZone.isValid ? parsedWithZone : DateTime.fromISO(endValue);

      if (!parsedEnd.isValid) {
        return false;
      }
      return parsedEnd.toUTC().toMillis() >= rangeStart;
    });

    return {
      events: filteredEvents,
      nextLink: json['@odata.nextLink'],
      deltaLink: json['@odata.deltaLink'],
    };
  }

  async fetchMicrosoftCalendarEventsDelta(
    calendarType: OAuthConnection,
    activeTab: string,
    deltaLink?: string,
  ): Promise<PagedCalendarEvents> {
    const accessToken = calendarType?.login_link;
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    let requestUrl = deltaLink;
    if (!requestUrl) {
      const { timeMin, timeMax } = this.getTimeMinMax(activeTab);
      const url = new URL(CalendarService.CALENDAR_MICROSOFT_DELTA_API);
      url.searchParams.set('startDateTime', timeMin);
      url.searchParams.set('endDateTime', timeMax);
      url.searchParams.set(
        '$select',
        'subject,start,end,id,isCancelled,location,body,recurrence,showAs,organizer,webLink,attendees,isAllDay,categories',
      );
      requestUrl = url.toString();
    }

    const response = await fetch(requestUrl, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
        Prefer: `outlook.timezone="${userTimeZone}"`,
      },
    });

    if (!response.ok) {
      const errorBody = await response.text();
      console.error(
        '[CalendarService] Microsoft Calendar delta fetch failed :',
        response.status,
        errorBody,
      );
      return {
        events: [],
        nextLink: undefined,
        deltaLink: undefined,
        authError: buildCalendarAuthError('Microsoft', response.status, errorBody),
      };
    }

    const json = await response.json();
    return {
      events: (json.value ?? []) as PagedCalendarEvents['events'],
      nextLink: json['@odata.nextLink'],
      deltaLink: json['@odata.deltaLink'],
    };
  }

  async updateGoogleEventWithMeetLink(accessToken: string, payload: unknown): Promise<void> {
    const {
      calendarId,
      provider: _provider,
      id,
      ...rest
    } = (payload ?? {}) as {
      id: string;
      calendarId?: string;
      provider?: string;
    } & Record<string, unknown>;

    if (!id) {
      throw new Error('Google calendar event id is required');
    }

    const resolvedCalendarId = encodeURIComponent(calendarId ?? 'primary');
    const encodedEventId = encodeURIComponent(id);
    const url = `${CalendarService.CALENDAR_GOOGLE_EVENTS_AND_LINK_API}/${resolvedCalendarId}/events/${encodedEventId}`;

    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(rest),
    });

    if (!response.ok) {
      const errorBody = await response.text();
      console.error(
        '[CalendarService] Error during update google event link :',
        response.status,
        errorBody,
      );
      throw new Error(`Google Calendar update failed (${response.status}): ${errorBody}`);
    }
  }

  /** Google list/delta responses omit calendarId; add the calendarid used in the fetch URL. */
  private updateGoogleEventsWithCalendarId(
    events: PagedCalendarEvents['events'],
    calendarId: string,
  ): PagedCalendarEvents['events'] {
    return events.map((event) => ({
      ...event,
      calendarId: event.calendarId ?? calendarId,
    }));
  }

  async updateMicrosoftEventWithMeetLink(accessToken: string, payload: unknown): Promise<void> {
    try {
      const {
        id,
        calendarId: _calendarId,
        provider: _provider,
        ...rest
      } = (payload ?? {}) as {
        id: string;
        calendarId?: string;
        provider?: string;
      } & Record<string, unknown>;

      const url = `${CalendarService.CALENDAR_MICROSOFT_LIST_EVENT_API}/${id}`;

      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(rest),
      });

      if (!response.ok) {
        const errorBody = await response.text();
        console.error(
          '[CalendarService] Error during update microsoft event link:',
          response.status,
          errorBody,
        );
      }
    } catch (error) {
      console.error('[CalendarService] Error during update microsoft event link:', error);
    }
  }

  async fetchGoogleCalendarColors(accessToken: string): Promise<unknown | null> {
    try {
      const response = await fetch(CalendarService.CALENDAR_GOOGLE_COLORS_API, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorBody = await response.text();
        console.error(
          '[CalendarService] Error during fetch google calendar colors:',
          response.status,
          errorBody,
        );
        return null;
      }

      const json = (await response.json()) as unknown;
      return json;
    } catch (error) {
      console.error('google calendar colors:', error);
      return null;
    }
  }

  async fetchMicrosoftCategories(accessToken: string): Promise<Record<string, unknown>[] | null> {
    try {
      const url = new URL(CalendarService.CALENDAR_MICROSOFT_MASTER_CATEGORIES_API);
      url.searchParams.set('$select', 'displayName,color');

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const errorBody = await response.text();
        console.error(
          '[CalendarService] Error during fetch microsoft master categories:',
          response.status,
          errorBody,
        );
        return null;
      }

      const json = await response.json();
      return json.value ?? [];
    } catch (error) {
      console.error('microsoft master categories:', error);
      return null;
    }
  }

  private async createGoogleEvent({
    formData,
    dialIn,
    accountConnections,
  }: Omit<
    CreateCalendarEventInBackgroundParams,
    'calendarOption'
  >): Promise<CreatedCalendarEvent | null> {
    const accessToken = accountConnections?.google?.login_link;
    if (!(accountConnections?.google?.connected && accessToken)) {
      return null;
    }

    const calendarId = accountConnections.google.email || 'primary';
    const encodedCalendarId = encodeURIComponent(calendarId);
    const password = formData.password ?? '';
    const invitationText = toPlainCalendarDescription(
      copyInvitationText(formData.room, dialIn, password),
    );
    const meetingUrl = getMeetingUrl(formData.room, password) || '';

    const payload = {
      summary: formData.description,
      description: invitationText,
      location: meetingUrl,
      start: { dateTime: toIso(formData.fromDate, formData.fromTime) },
      end: { dateTime: toIso(formData.toDate, formData.toTime) },
      ...(formData.selectedColor?.trim() ? { colorId: formData.selectedColor.trim() } : {}),
    };

    const response = await fetch(
      `${CalendarService.CALENDAR_GOOGLE_EVENTS_AND_LINK_API}/${encodedCalendarId}/events`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      },
    );

    if (!response.ok) {
      throw new Error(`Google calendar event creation failed: ${response.status}`);
    }

    const event = (await response.json()) as { id?: string; htmlLink?: string };
    if (!event.id) {
      return null;
    }

    return {
      id: event.id,
      calendarId,
      provider: 'google',
      htmlLink: event.htmlLink,
    };
  }

  private async createMicrosoftEvent({
    formData,
    dialIn,
    accountConnections,
  }: Omit<
    CreateCalendarEventInBackgroundParams,
    'calendarOption'
  >): Promise<CreatedCalendarEvent | null> {
    const accessToken = accountConnections?.microsoft?.login_link;
    if (!(accountConnections?.microsoft?.connected && accessToken)) {
      return null;
    }

    const password = formData.password ?? '';
    const invitationText = toPlainCalendarDescription(
      copyInvitationText(formData.room, dialIn, password),
    );
    const meetingUrl = getMeetingUrl(formData.room, password) || '';
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

    const payload = {
      subject: formData.description,
      body: {
        contentType: 'Text',
        content: invitationText,
      },
      start: {
        dateTime: toLocalDateTime(formData.fromDate, formData.fromTime),
        timeZone: timezone,
      },
      end: {
        dateTime: toLocalDateTime(formData.toDate, formData.toTime),
        timeZone: timezone,
      },
      location: {
        displayName: meetingUrl,
      },
      ...(formData.selectedColor?.trim() ? { categories: [formData.selectedColor.trim()] } : {}),
    };

    const response = await fetch(CalendarService.CALENDAR_MICROSOFT_LIST_EVENT_API, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`Microsoft calendar event creation failed: ${response.status}`);
    }

    const event = (await response.json()) as { id?: string; webLink?: string };
    if (!event.id) {
      return null;
    }

    return {
      id: event.id,
      provider: 'microsoft',
      htmlLink: event.webLink,
    };
  }

  async createCalendarEventInBackground(
    params: CreateCalendarEventInBackgroundParams,
  ): Promise<CreatedCalendarEvent | null> {
    if (params.calendarOption === 'google') {
      return this.createGoogleEvent(params);
    }

    if (params.calendarOption === 'outlook') {
      return this.createMicrosoftEvent(params);
    }

    return null;
  }

  public refreshAuthToken(): void {
    // Hosted/MF mode registers a tokenProvider; writing a static Authorization
    // from local storage would defeat per-request host tokens.
    if (this.tokenProvider) {
      return;
    }
    const token = CalendarService.getAuthToken();
    this.setAuthToken(token);
  }
}

export const calendarService = new CalendarService();
